import React from "react";
import {
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { 
  BadgeRounded, 
  AccountBoxRounded, 
  LocalPhoneRounded, 
  PeopleRounded,
  BusinessRounded,
  LocationOnRounded,
  HomeRounded,
  MarkunreadMailboxRounded,
  CheckCircleOutline,
  ArrowForward,
} from "@mui/icons-material";
import logo from "../../assets/logo.png";

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: 'white',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'grey.300',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'grey.500',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'success.light',
      borderWidth: '1px', 
    },
  },
};

const labelStyles = { 
  fontWeight: 'bold', 
  mb: 0.5, 
  display: 'block',
  color: 'text.primary',
};

interface RegistrationHeaderProps {
  currentStep: number;
  progress: number;
}

export const RegistrationHeader: React.FC<RegistrationHeaderProps> = ({ 
  currentStep, 
  progress 
}) => (
  <Box className="registration-header">
    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
      <img
        src={logo}
        alt="Logo de la empresa"
        style={{
          height: "80px",
          maxWidth: "200px",
          objectFit: "contain",
        }}
      />
    </Box>

    <Typography variant="h4" component="h1" gutterBottom>
      {currentStep === 1 ? "Completa tu Registro" : "Datos de la Empresa"}
    </Typography>
    <Typography variant="body1" color="text.secondary" paragraph>
      {currentStep === 1 
        ? "Por favor, ingresa tus datos para completar tu perfil de forma segura en nuestra plataforma y poderte brindar soporte de integración."
        : "Puedes completar estos datos ahora o saltar este paso."
      }
    </Typography>

    <Box sx={{ width: "100%", mt: 2, mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
          Progreso del registro
        </Typography>
        <Typography variant="body2" color="#4caf50" fontWeight="bold">
          {Math.round(progress)}% completado
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "rgba(0,0,0,0.1)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 4,
            background: "linear-gradient(135deg, #4caf50 0%, #81c784 100%)",
          },
        }}
      />
    </Box>
  </Box>
);

interface FormErrors {
    first_name: boolean;
    last_name: boolean;
    phone: boolean;
    email: boolean;
    postal_code: boolean;
    country: boolean;
    street: boolean;
    ext_number: boolean;
    district: boolean;
    city: boolean;
    state: boolean;
}

interface PersonalInfoFormProps {
    firstName: string;
    setFirstName: (value: string) => void;
    lastName: string;
    setLastName: (value: string) => void;
    phone: string;
    setPhone: (value: string) => void;
    email: string;
    setEmail: (value: string) => void;
    recommended: string;
    setRecommended: (value: string) => void;
    errors: FormErrors;
    touched: FormErrors;
    handleBlur: (field: keyof FormErrors) => void;
    getHelperText: (field: keyof FormErrors) => string;
    isStep1Valid: boolean;
    handleNextStep: () => void;
}

export const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
    firstName, setFirstName,
    lastName, setLastName,
    phone, setPhone,
    email, setEmail,
    recommended, setRecommended,
    errors, touched,
    handleBlur, getHelperText,
    isStep1Valid, handleNextStep
}) => {
  const { logout } = useAuth0();

  return (
  <Box className="registration-form">
    <Box className="form-section">
      <Typography variant="h6" gutterBottom>
        Información
      </Typography>

      <Stack 
        spacing={{ xs: 1, sm: 2 }}
        direction="row"
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-nombre" sx={labelStyles}>
            Nombre
          </Typography>
          <TextField
            id="input-nombre"
            type="text"
            variant="outlined"
            placeholder="Escribe tu nombre"
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => handleBlur("first_name")}
            value={firstName}
            size="small"
            fullWidth
            required
            error={touched.first_name && errors.first_name}
            helperText={getHelperText("first_name")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountBoxRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-apellido" sx={labelStyles}>
            Apellido
          </Typography>
          <TextField
            id="input-apellido"
            type="text"
            variant="outlined"
            placeholder="Escribe tu apellido"
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => handleBlur("last_name")}
            value={lastName}
            size="small"
            fullWidth
            required
            error={touched.last_name && errors.last_name}
            helperText={getHelperText("last_name")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-telefono" sx={labelStyles}>
            Número Telefónico
          </Typography>
          <TextField
            id="input-telefono"
            type="tel"
            variant="outlined"
            placeholder="1234567890"
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => handleBlur("phone")}
            value={phone}
            size="small"
            fullWidth
            required
            error={touched.phone && errors.phone}
            helperText={getHelperText("phone")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalPhoneRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-email" sx={labelStyles}>
            Correo Electrónico
          </Typography>
          <TextField
            id="input-email"
            type="email"
            variant="outlined"
            placeholder="example@example.com"
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur("email")}
            value={email}
            size="small"
            fullWidth
            required
            error={touched.email && errors.email}
            helperText={getHelperText("email")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MarkunreadMailboxRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ 
          width: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          p: 1.5, 
          bgcolor: '#f2fcf4', 
          borderRadius: '12px',
          mt: 1
        }}>
          <CheckCircleOutline sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Toda tu información está protegida por protocolos de seguridad de nivel bancario. No compartiremos tus datos con terceros.
          </Typography>
        </Box>
      </Stack>
    </Box>

    <Box className="form-section">
      <Typography variant="h6" gutterBottom>
        ¿Cómo nos conociste?
      </Typography>

      <Box sx={{ width: '100%' }}>
        <FormControl fullWidth required>
          <Select
            id="input-recomendacion"
            size="small"
            value={recommended}
            onChange={(e) => setRecommended(e.target.value)}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <PeopleRounded sx={{ color: 'grey.500' }} />
              </InputAdornment>
            }
            sx={{
              borderRadius: '12px',
              bgcolor: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.500',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'success.light',
                borderWidth: '1px', 
              },
            }}
          >
            //simplificar con un map
            <MenuItem value=""><em>Selecciona una opción</em></MenuItem>
            <MenuItem value="Instagram">Instagram</MenuItem>
            <MenuItem value="Facebook">Facebook</MenuItem>
            <MenuItem value="Tik Tok">Tik Tok</MenuItem>
            <MenuItem value="Google">Google</MenuItem>
            <MenuItem value="Recomendado">Recomendado</MenuItem>
            <MenuItem value="Publicidad Fisica">Publicidad Física</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>

    <Box className="form-actions">
      <Button
        variant="contained"
        fullWidth
        size="large"
        disabled={!isStep1Valid}
        onClick={handleNextStep}
        endIcon={<ArrowForward />}
        sx={{
          borderRadius: '50px',
          py: 1.5,
          background: isStep1Valid 
            ? 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)'
            : '#e0e0e0',
          '&:hover': {
            background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
          },
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '1rem',
        }}
      >
        Continuar
      </Button>
    </Box>

    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Typography
        variant="body2"
        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        sx={{ color: 'error.main', cursor: 'pointer', fontWeight: 'bold' }}
      >
        Cerrar Sesión
      </Typography>
    </Box>

    <SupportLink />
  </Box>
  );
};

interface EnterpriseFormProps {
  enterpriseSocialReason: string;
  setEnterpriseSocialReason: (value: string) => void;
  enterprisePostalCode: string;
  setEnterprisePostalCode: (value: string) => void;
  enterpriseStreet: string;
  setEnterpriseStreet: (value: string) => void;
  enterpriseExtNumber: string;
  setEnterpriseExtNumber: (value: string) => void;
  enterpriseDistrict: string;
  setEnterpriseDistrict: (value: string) => void;
  enterpriseState: string;
  setEnterpriseState: (value: string) => void;
  onChangeEnterpriseZip: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isSubmitting: boolean;
  handleSubmit: (event: React.FormEvent) => void;
  handlePrevStep: () => void;
}

export const EnterpriseForm: React.FC<EnterpriseFormProps> = ({
  enterpriseSocialReason, setEnterpriseSocialReason,
  enterprisePostalCode, setEnterprisePostalCode,
  enterpriseStreet, setEnterpriseStreet,
  enterpriseExtNumber, setEnterpriseExtNumber,
  enterpriseDistrict, setEnterpriseDistrict,
  enterpriseState, setEnterpriseState,
  onChangeEnterpriseZip,
  isSubmitting, handleSubmit, handlePrevStep
}) => {
  const { logout } = useAuth0();

  return (
  <form onSubmit={handleSubmit} className="registration-form">
    <Box className="form-section">
      <Typography variant="h6" gutterBottom>
        Información de la Empresa
      </Typography>

      <Stack 
        spacing={{ xs: 1, sm: 2 }}
        direction="row"
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-razon-social" sx={labelStyles}>
            Razón Social
          </Typography>
          <TextField
            id="input-razon-social"
            type="text"
            variant="outlined"
            placeholder="Nombre de la empresa"
            onChange={(e) => setEnterpriseSocialReason(e.target.value)}
            value={enterpriseSocialReason}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-cp-empresa" sx={labelStyles}>
            Código Postal
          </Typography>
          <TextField
            id="input-cp-empresa"
            type="text"
            variant="outlined"
            placeholder="12345"
            onChange={(e) => {
              setEnterprisePostalCode(e.target.value);
              onChangeEnterpriseZip(e);
            }}
            value={enterprisePostalCode}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-calle-empresa" sx={labelStyles}>
            Calle
          </Typography>
          <TextField
            id="input-calle-empresa"
            type="text"
            variant="outlined"
            placeholder="Nombre de la calle"
            onChange={(e) => setEnterpriseStreet(e.target.value)}
            value={enterpriseStreet}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HomeRounded sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'calc(33.33% - 11px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-num-ext-empresa" sx={labelStyles}>
            Número Exterior
          </Typography>
          <TextField
            id="input-num-ext-empresa"
            type="text"
            variant="outlined"
            placeholder="123"
            onChange={(e) => setEnterpriseExtNumber(e.target.value)}
            value={enterpriseExtNumber}
            size="small"
            fullWidth
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'calc(33.33% - 11px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-colonia-empresa" sx={labelStyles}>
            Colonia
          </Typography>
          <TextField
            id="input-colonia-empresa"
            type="text"
            variant="outlined"
            placeholder="Colonia"
            onChange={(e) => setEnterpriseDistrict(e.target.value)}
            value={enterpriseDistrict}
            size="small"
            fullWidth
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'calc(33.33% - 11px)' } }}>
          <Typography variant="subtitle2" component="label" htmlFor="input-estado-empresa" sx={labelStyles}>
            Estado
          </Typography>
          <TextField
            id="input-estado-empresa"
            type="text"
            variant="outlined"
            placeholder="Estado"
            onChange={(e) => setEnterpriseState(e.target.value)}
            value={enterpriseState}
            size="small"
            fullWidth
            sx={textFieldStyles}
          />
        </Box>

        <Box sx={{ 
          width: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          p: 1.5, 
          bgcolor: '#f2fcf4', 
          borderRadius: '12px',
          mt: 1
        }}>
          <CheckCircleOutline sx={{ color: 'success.main', fontSize: 20 }} />
          <Typography variant="body2" color="text.secondary">
            Estos datos son opcionales y puedes editarlos más tarde desde la configuración de tu perfil.
          </Typography>
        </Box>
      </Stack>
    </Box>

    <Box className="form-actions">
      <Button
        variant="contained"
        type="submit"
        fullWidth
        size="large"
        disabled={isSubmitting}
        endIcon={<ArrowForward />}
        sx={{
          borderRadius: '50px',
          py: 1.5,
          background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
          },
          textTransform: 'none',
          fontWeight: 'bold',
          fontSize: '1rem',
        }}
      >
        {isSubmitting ? "Guardando..." : "Continuar"}
      </Button>
    </Box>

    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Button
        variant="text"
        onClick={handlePrevStep}
        sx={{ 
          color: 'text.secondary',
          textTransform: 'none',
        }}
      >
        Volver atrás
      </Button>
    </Box>

    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Typography
        variant="body2"
        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        sx={{ color: 'error.main', cursor: 'pointer', fontWeight: 'bold' }}
      >
        Cerrar Sesión
      </Typography>
    </Box>

    <SupportLink />
  </form>
  );
};

const SupportLink: React.FC = () => (
  <Box sx={{ textAlign: 'center', mt: 2 }}>
    <Typography variant="body2" color="text.secondary">
      ¿Necesitas ayuda con el registro?{' '}
      <Typography 
        component="a"
        href="https://www.pagosfiable.com/#contact"
        target="_blank"
        rel="noopener noreferrer"
        variant="body2" 
        sx={{ color: 'success.main', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'none' }}
      >
        Contactar soporte
      </Typography>
    </Typography>
  </Box>
);
